import dayjs from "dayjs";

// EO-1184 — the only expiry maths the frontend needs: telling an extension
// apart from any other promo edit, so promo_extended fires on extensions alone.

// Reduce to a bare calendar day. Promo windows are date-granular, and the two
// sides of a comparison can arrive in different shapes ("2026-09-01" from one
// endpoint, an ISO instant from another) — normalising both avoids a stray time
// component reading as a change.
const toDay = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    const dateOnly = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
    if (dateOnly) return dayjs(`${dateOnly[1]}T00:00:00`);
  }
  const parsed = dayjs(value);
  return parsed.isValid()
    ? dayjs(`${parsed.format("YYYY-MM-DD")}T00:00:00`)
    : null;
};

/**
 * The furthest stay end date across a promo's stay date ranges (EO-1492
 * multi-range). The legacy single stayEndDate is seeded from the FIRST range
 * only, and ranges are stored in creation order, not date order — so it cannot
 * stand in for the promo's real stay end once stayDateRanges is populated.
 *
 * Mirrors the backend SDK's PromoExpiryRules.latestEnd: ranges without an end
 * date are unbounded and ignored; no bounded end at all means evergreen.
 *
 * @param {Object} stayVO
 * @returns {string|null} the stored date string of the furthest range end
 */
export const getFurthestStayEndDate = (stayVO) => {
  const ranges = stayVO?.stayDateRanges;
  if (!Array.isArray(ranges) || ranges.length === 0) {
    return stayVO?.stayEndDate || null;
  }
  let furthest = null;
  let furthestDay = null;
  ranges.forEach((range) => {
    const day = toDay(range?.stayEndDate);
    if (day && (!furthestDay || day.isAfter(furthestDay))) {
      furthest = range.stayEndDate;
      furthestDay = day;
    }
  });
  return furthest;
};

/**
 * The earliest stay start date across the ranges — the display counterpart of
 * {@link getFurthestStayEndDate} for surfaces that render one stay window.
 *
 * @param {Object} stayVO
 * @returns {string|null}
 */
export const getEarliestStayStartDate = (stayVO) => {
  const ranges = stayVO?.stayDateRanges;
  if (!Array.isArray(ranges) || ranges.length === 0) {
    return stayVO?.stayStartDate || null;
  }
  let earliest = null;
  let earliestDay = null;
  ranges.forEach((range) => {
    const day = toDay(range?.stayStartDate);
    if (day && (!earliestDay || day.isBefore(earliestDay))) {
      earliest = range.stayStartDate;
      earliestDay = day;
    }
  });
  return earliest;
};

/**
 * The date a promo's window closes, off a full promo VO — the shape the
 * create/edit drawer edits and the promo detail endpoints return. Booking end
 * wins over stay end (EO-1184 date-precedence rule); the stay end is the
 * furthest across all stay date ranges, matching the backend SDK rule.
 *
 * @param {Object} promoVo
 * @returns {string|null} null for an evergreen promo
 */
export const getPromoVoExpiryDate = (promoVo) => {
  const ruleVO = promoVo?.rateRule?.ruleVO;
  if (!ruleVO) return null;
  return (
    ruleVO.bookingVO?.bookingEndDate ||
    getFurthestStayEndDate(ruleVO.stayVO) ||
    null
  );
};

/**
 * True when a save pushed the promo's window further out — i.e. the edit was an
 * extension rather than any other change.
 *
 * A promo that had no end date cannot be extended: there was nothing to push
 * out, and adding one is a narrowing, not an extension.
 *
 * @param {string|null} previousExpiryDate
 * @param {string|null} nextExpiryDate
 * @returns {boolean}
 */
export const isExtension = (previousExpiryDate, nextExpiryDate) => {
  const before = toDay(previousExpiryDate);
  const after = toDay(nextExpiryDate);
  if (!before || !after) return false;
  return after.isAfter(before);
};
